/**
 * Admin Users Hooks
 *
 * Custom hooks for admin user management.
 *
 * @fileoverview React hooks for admin user operations
 */

import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../services/adminApi';
import {
  AdminUser,
  UserQueryParams,
  PaginatedResponse,
  UserStats,
  UpdateAdminUserInput,
  CreateAdminUserInviteInput,
  GlobalRole,
  UpdateMembershipInput,
  AddMembershipInput,
} from '../types/admin';
import { toUserMessage } from '../lib/apiError';

/**
 * Hook for managing user list with pagination, search, and filtering
 *
 * @param initialParams - Initial query parameters
 * @returns User list state and control functions
 *
 * @example
 * const { users, loading, pagination, setSearch, setPage } = useAdminUsers();
 */
export function useAdminUsers(initialParams: UserQueryParams = {}) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: initialParams.page || 1,
    limit: initialParams.limit || 10,
    total: 0,
    totalPages: 0,
  });
  const [params, setParams] = useState<UserQueryParams>({
    ...initialParams,
    sortBy: initialParams.sortBy || 'createdAt',
    sortOrder: initialParams.sortOrder || 'desc',
  });

  /**
   * Fetch users from API
   */
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response: PaginatedResponse<AdminUser> = await adminApi.users.list(params);
      setUsers(response.data);
      setPagination({
        page: response.page,
        limit: response.limit,
        total: response.total,
        totalPages: response.totalPages,
      });
    } catch (err) {
      setError(toUserMessage(err, "We couldn't load users. Please try again."));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  /**
   * Set search string
   */
  const setSearch = useCallback((search: string) => {
    setParams((prev) => ({ ...prev, search, page: 1 }));
  }, []);

  /**
   * Set active status filter
   */
  const setIsActive = useCallback((isActive: boolean | undefined) => {
    setParams((prev) => ({ ...prev, isActive, page: 1 }));
  }, []);

  /**
   * Set global role filter
   */
  const setGlobalRole = useCallback((globalRole: GlobalRole | undefined) => {
    setParams((prev) => ({ ...prev, globalRole, page: 1 }));
  }, []);

  /**
   * Set tenant filter
   */
  const setTenantId = useCallback((tenantId: string | undefined) => {
    setParams((prev) => ({ ...prev, tenantId, page: 1 }));
  }, []);

  /**
   * Set page number
   */
  const setPage = useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }));
  }, []);

  /**
   * Set items per page
   */
  const setLimit = useCallback((limit: number) => {
    setParams((prev) => ({ ...prev, limit, page: 1 }));
  }, []);

  /**
   * Set sort field and order
   */
  const setSort = useCallback(
    (sortBy: UserQueryParams['sortBy'], sortOrder: UserQueryParams['sortOrder']) => {
      setParams((prev) => ({ ...prev, sortBy, sortOrder }));
    },
    []
  );

  /**
   * Reset all filters to defaults
   */
  const resetFilters = useCallback(() => {
    setParams({
      page: 1,
      limit: 10,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    });
  }, []);

  /**
   * Refetch users
   */
  const refetch = useCallback(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    pagination,
    filters: params,
    setSearch,
    setIsActive,
    setGlobalRole,
    setTenantId,
    setPage,
    setLimit,
    setSort,
    resetFilters,
    refetch,
  };
}

/**
 * Hook for managing a single user
 *
 * @param id - User UUID
 * @returns User state and control functions
 *
 * @example
 * const { user, loading, updateUser, deleteUser } = useAdminUser(userId);
 */
export function useAdminUser(id: string | undefined) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch user from API
   */
  const fetchUser = useCallback(async () => {
    if (!id) {
      setUser(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await adminApi.users.getById(id);
      setUser(data);
    } catch (err) {
      setError(toUserMessage(err, "We couldn't load the user. Please try again."));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  /**
   * Update user
   */
  const updateUser = useCallback(
    async (data: UpdateAdminUserInput) => {
      if (!id) return null;

      setLoading(true);
      setError(null);

      try {
        const updated = await adminApi.users.update(id, data);
        setUser(updated);
        return updated;
      } catch (err) {
        setError(toUserMessage(err, "We couldn't update the user. Please try again."));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [id]
  );

  /**
   * Delete user
   */
  const deleteUser = useCallback(async () => {
    if (!id) return false;

    setLoading(true);
    setError(null);

    try {
      await adminApi.users.delete(id);
      return true;
    } catch (err) {
      if (err instanceof Error && (err.message.includes('404') || err.message.includes('Not Found'))) {
        return true;
      }
      setError(toUserMessage(err, "We couldn't delete the user. Please try again."));
      return false;
    } finally {
      setLoading(false);
    }
  }, [id]);

  /**
   * Activate user
   */
  const activateUser = useCallback(async () => {
    if (!id) return null;

    setLoading(true);
    setError(null);

    try {
      const updated = await adminApi.users.activate(id);
      setUser(updated);
      return updated;
    } catch (err) {
      setError(toUserMessage(err, "We couldn't activate the user. Please try again."));
      return null;
    } finally {
      setLoading(false);
    }
  }, [id]);

  /**
   * Deactivate user
   */
  const deactivateUser = useCallback(async () => {
    if (!id) return null;

    setLoading(true);
    setError(null);

    try {
      const updated = await adminApi.users.deactivate(id);
      setUser(updated);
      return updated;
    } catch (err) {
      setError(toUserMessage(err, "We couldn't deactivate the user. Please try again."));
      return null;
    } finally {
      setLoading(false);
    }
  }, [id]);

  /**
   * Send password reset email
   */
  const sendPasswordReset = useCallback(async () => {
    if (!id) return null;

    setLoading(true);
    setError(null);

    try {
      const result = await adminApi.users.sendPasswordReset(id);
      return result;
    } catch (err) {
      setError(toUserMessage(err, "We couldn't send the password reset email. Please try again."));
      return null;
    } finally {
      setLoading(false);
    }
  }, [id]);

  return {
    user,
    loading,
    error,
    refetch: fetchUser,
    updateUser,
    deleteUser,
    activateUser,
    deactivateUser,
    sendPasswordReset,
  };
}

/**
 * Hook for user statistics
 *
 * @returns User statistics state
 *
 * @example
 * const { stats, loading } = useAdminUserStats();
 */
export function useAdminUserStats() {
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch statistics from API
   */
  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await adminApi.users.getStats();
      setStats(data);
    } catch (err) {
      setError(toUserMessage(err, "We couldn't load the statistics. Please try again."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch: fetchStats,
  };
}

/**
 * Hook for inviting a new user (requires tenant)
 *
 * @returns Invite function and state
 *
 * @example
 * const { inviteUser, loading, error } = useInviteAdminUser();
 * const invite = await inviteUser({ email: 'user@example.com', tenantId: 'uuid' });
 */
export function useInviteAdminUser() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Invite a new user to a tenant
   */
  const inviteUser = useCallback(async (data: CreateAdminUserInviteInput) => {
    setLoading(true);
    setError(null);

    try {
      const invite = await adminApi.users.invite(data);
      return invite;
    } catch (err) {
      setError(toUserMessage(err, "We couldn't send the invite. Please try again."));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    inviteUser,
    loading,
    error,
    clearError: () => setError(null),
  };
}

/**
 * Hook for recent users (dashboard)
 *
 * @param limit - Number of users to fetch
 * @returns Recent users state
 *
 * @example
 * const { users, loading } = useRecentUsers(5);
 */
export function useRecentUsers(limit: number = 5) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch recent users from API
   */
  const fetchRecent = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await adminApi.users.getRecent(limit);
      setUsers(data);
    } catch (err) {
      setError(toUserMessage(err, "We couldn't load recent users. Please try again."));
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchRecent();
  }, [fetchRecent]);

  return {
    users,
    loading,
    error,
    refetch: fetchRecent,
  };
}

/**
 * Hook for user actions (activate, deactivate, delete, password reset)
 *
 * @returns Action functions and state
 *
 * @example
 * const { activateUser, deactivateUser, deleteUser } = useUserActions();
 */
export function useUserActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Activate a user
   */
  const activateUser = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const user = await adminApi.users.activate(id);
      return user;
    } catch (err) {
      setError(toUserMessage(err, "We couldn't activate the user. Please try again."));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Deactivate a user
   */
  const deactivateUser = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const user = await adminApi.users.deactivate(id);
      return user;
    } catch (err) {
      setError(toUserMessage(err, "We couldn't deactivate the user. Please try again."));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete a user
   */
  const deleteUser = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      await adminApi.users.delete(id);
      return true;
    } catch (err) {
      if (err instanceof Error && (err.message.includes('404') || err.message.includes('Not Found'))) {
        return true;
      }
      setError(toUserMessage(err, "We couldn't delete the user. Please try again."));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Send password reset email
   */
  const sendPasswordReset = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const result = await adminApi.users.sendPasswordReset(id);
      return result;
    } catch (err) {
      setError(toUserMessage(err, "We couldn't send the password reset email. Please try again."));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update a user
   */
  const updateUser = useCallback(async (id: string, data: UpdateAdminUserInput) => {
    setLoading(true);
    setError(null);

    try {
      const user = await adminApi.users.update(id, data);
      return user;
    } catch (err) {
      setError(toUserMessage(err, "We couldn't update the user. Please try again."));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update a user's tenant membership
   */
  const updateMembership = useCallback(async (userId: string, tenantId: string, data: UpdateMembershipInput) => {
    setLoading(true);
    setError(null);

    try {
      const user = await adminApi.users.updateMembership(userId, tenantId, data);
      return user;
    } catch (err) {
      setError(toUserMessage(err, "We couldn't update the membership. Please try again."));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Add a user to a tenant
   */
  const addMembership = useCallback(async (userId: string, data: AddMembershipInput) => {
    setLoading(true);
    setError(null);

    try {
      const user = await adminApi.users.addMembership(userId, data);
      return user;
    } catch (err) {
      setError(toUserMessage(err, "We couldn't add the membership. Please try again."));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Remove a user from a tenant
   */
  const removeMembership = useCallback(async (userId: string, tenantId: string) => {
    setLoading(true);
    setError(null);

    try {
      const user = await adminApi.users.removeMembership(userId, tenantId);
      return user;
    } catch (err) {
      setError(toUserMessage(err, "We couldn't remove the membership. Please try again."));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    activateUser,
    deactivateUser,
    deleteUser,
    sendPasswordReset,
    updateUser,
    updateMembership,
    addMembership,
    removeMembership,
    loading,
    error,
    clearError: () => setError(null),
  };
}
